import React, { useEffect, useState } from 'react';
import { Box, Typography, useTheme, Select, MenuItem, FormControl, IconButton } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { authService, SELECTED_CLIENT_KEY } from '../../services/auth.service';

// Ocultar el selector de cliente al enseñar la app a un cliente potencial: si no, ve la lista de
// los demás clientes. Se recuerda entre recargas para que no reaparezca a mitad de la demo.
const HIDE_CLIENT_SWITCHER_KEY = 'hide_client_switcher';

const whiteSelectSx = {
  color: 'white',
  '.MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.23)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: 'rgba(255, 255, 255, 0.4)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: 'white',
  },
  '.MuiSelect-icon': {
    color: 'white',
  },
};

/**
 * Header component for page titles and tabs
 * 
 * @param {Object} props - Component props
 * @param {string} props.title - Page title
 * @param {Array} props.tabs - Array of tab objects with label properties
 * @param {number} props.activeTab - Index of the active tab
 * @param {Function} props.onTabChange - Function to handle tab changes
 * @param {boolean} props.showTabs - Whether to show tabs (default: true)
 */
const Header = ({ title, showMenuButton = false, onMenuClick = () => {} }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.is_admin === true;
  const [clients, setClients] = useState([]);
  const [clientSwitcherHidden, setClientSwitcherHidden] = useState(
    () => localStorage.getItem(HIDE_CLIENT_SWITCHER_KEY) === 'true'
  );

  useEffect(() => {
    if (!isAdmin) return;
    authService.getClients().then(setClients).catch(() => setClients([]));
  }, [isAdmin]);

  const handleLanguageChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  // Switching client reloads the whole app with the new client; the login is kept.
  const handleClientChange = (event) => {
    localStorage.setItem(SELECTED_CLIENT_KEY, event.target.value);
    window.location.reload();
  };
  const clientOptions = [...new Set([...clients, currentUser?.customer_id].filter(Boolean))];
  const toggleClientSwitcher = () => {
    const next = !clientSwitcherHidden;
    localStorage.setItem(HIDE_CLIENT_SWITCHER_KEY, String(next));
    setClientSwitcherHidden(next);
  };
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        padding: { xs: `${theme.spacing(1.5)} ${theme.spacing(2)}`, md: `${theme.spacing(2)} ${theme.spacing(3)}` },
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
        {showMenuButton && (
          <IconButton
            onClick={onMenuClick}
            edge="start"
            aria-label={t('openMenu')}
            sx={{ color: 'inherit', mr: 0.5 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Typography
          variant="h5"
          component="div"
          noWrap
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: { xs: '1.1rem', sm: '1.25rem', md: theme.typography.h5.fontSize },
            paddingLeft: theme.spacing(1),
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {title}
        </Typography>
      </Box>

      {isAdmin && clientOptions.length > 0 && (
        <IconButton
          size="small"
          onClick={toggleClientSwitcher}
          sx={{ color: 'inherit', opacity: 0.6, flexShrink: 0, '&:hover': { opacity: 1 } }}
        >
          {clientSwitcherHidden
            ? <VisibilityOutlinedIcon fontSize="small" />
            : <VisibilityOffOutlinedIcon fontSize="small" />}
        </IconButton>
      )}

      {isAdmin && clientOptions.length > 0 && !clientSwitcherHidden && (
        <FormControl size="small" sx={{ minWidth: { xs: 92, sm: 120 }, flexShrink: 0 }}>
          <Select
            value={currentUser.customer_id}
            onChange={handleClientChange}
            inputProps={{ 'aria-label': t('switchClient') }}
            sx={whiteSelectSx}
          >
            {clientOptions.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
      )}

      <FormControl size="small" sx={{ minWidth: { xs: 92, sm: 120 }, flexShrink: 0 }}>
        <Select
          value={i18n.language}
          onChange={handleLanguageChange}
          sx={whiteSelectSx}
        >
          <MenuItem value="en">English</MenuItem>
          <MenuItem value="es">Español</MenuItem>
          <MenuItem value="it">Italiano</MenuItem>
        </Select>
      </FormControl>
    </Box>
  );
};

export default Header;